import { Cell, Pie, PieChart } from 'recharts';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetEventDemographicsStatsQuery as GetEventDemographicsStats } from '__generated__/graphql';

import { FilterIndicator, StatBar } from './Layout';
import { assetUrl } from '../../common/helpers';
import { useColors } from './helpers';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';

type Event = GetEventDemographicsStats['event'];

export interface RepeaterStatsProps {
  event: Event;
}

const RepeaterStats = memo(({ event }: RepeaterStatsProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatNumber } = useLocale();

  const formatPercentage = (number: number) => (
    `${formatNumber(number, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
  );

  const total = event.registrations_count;
  const returningParticipants = event.analytics.repeaters.count;
  const newParticipants = total - returningParticipants;

  const data = [
    { name: 'new', count: newParticipants },
    { name: 'returning', count: returningParticipants },
  ];

  const perPage = 3;
  const { events } = event.analytics.repeaters;
  const [limit, setLimit] = useState(perPage);
  const visibleEvents = events.slice(0, limit + perPage);

  const next = () => setLimit((limit) => limit + perPage);
  const prev = () => setLimit((limit) => limit - perPage);

  const barColors = useColors(-100);
  const labelColors = useColors();

  const hasData = total - newParticipants > 0;

  return (
    <UI.Card sc={{ hasPanels: true }}>
      <UI.GridContainer sc={{ gutter: 0.75 }} style={{ height: '100%' }}>
        <UI.Div>
          <UI.FlexContainer sc={{ alignItems: 'center', mb: 1 }}>
            <UI.H4>
              {t('returning_participants')}
            </UI.H4>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>
          <UI.Div sc={{ color: 'gray.500' }}>
            {t('returning_participants_description')}
          </UI.Div>
        </UI.Div>

        {hasData && (
          <>
            <UI.GridContainer
              sc={{ columns: '120px 1fr 75px 48px', alignVertical: 'center', gutter: 0 }}
              style={{ marginBottom: 'auto' }}
            >
              <UI.Div style={{ gridRow: '1 / span 4' }}>
                <PieChart width={90} height={90}>
                  <Pie
                    data={data}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={45}
                    startAngle={-270}
                  >
                    {data.map((entry, index) => (
                      <Cell fill={barColors[index]} key={index} />
                    ))}
                  </Pie>
                </PieChart>
              </UI.Div>

              <UI.Div sc={{ fontWeight: 500 }} style={{ color: labelColors[1], gridRow: '2' }}>
                {t('returning')}
              </UI.Div>
              <UI.Div style={{ gridRow: '2' }}>
                {formatInteger(returningParticipants)}
              </UI.Div>
              <UI.Div sc={{ muted: true, small: true }} style={{ gridRow: '2' }}>
                {total > 0 && formatPercentage((returningParticipants / total) * 100)}
              </UI.Div>

              <UI.Div sc={{ fontWeight: 500 }} style={{ color: labelColors[0], gridRow: '3' }}>
                {t('new')}
              </UI.Div>
              <UI.Div style={{ gridRow: '3' }}>
                {formatInteger(newParticipants)}
              </UI.Div>
              <UI.Div sc={{ muted: true, small: true }} style={{ gridRow: '3' }}>
                {total > 0 && formatPercentage(100 - (returningParticipants / total) * 100)}
              </UI.Div>
            </UI.GridContainer>

            {events.length > 0 && (
              <UI.Div style={{ height: Math.min(events.length, perPage) * 28, overflow: 'hidden' }}>
                <UI.GridContainer
                  sc={{ gutter: 0 }}
                  style={{
                    marginTop: -(limit - perPage) * 28,
                    transition: 'margin 0.15s ease-in-out',
                  }}
                >
                  {visibleEvents.map((data) => (
                    <UI.GridContainer
                      sc={{ columns: '120px 6fr 75px 48px', gutter: 0, alignVertical: 'center' }}
                      key={data.event.id}
                    >
                      <UI.Div
                        sc={{ color: 'gray.800', pr: 2 }}
                        style={{ gridColumn: '1 / span 2', minWidth: 0 }}
                      >
                        <StatBar values={[data.count]} max={total} colors={[barColors[1]]}>
                          <UI.Truncate>
                            {data.event.title}
                          </UI.Truncate>
                        </StatBar>
                      </UI.Div>
                      <UI.Div>
                        {formatInteger(data.count)}
                      </UI.Div>
                      <UI.Div sc={{ muted: true, small: true }}>
                        {total > 0 && formatPercentage((data.count / total) * 100)}
                      </UI.Div>
                    </UI.GridContainer>
                  ))}
                </UI.GridContainer>
              </UI.Div>
            )}

            {events.length === 0 && (
              <UI.Div sc={{ muted: true, mt: 2 }}>
                {t('no_returning_participants')}
              </UI.Div>
            )}

            {(limit < events.length || limit > perPage) && (
              <UI.Div style={{ marginTop: 'auto' }}>
                <UI.Button
                  onClick={() => prev()}
                  sc={{ blank: true, size: 'xs' }}
                  disabled={limit <= perPage}
                >
                  <UI.Icon sc={{ muted: true }}>
                    <ChevronLeft />
                  </UI.Icon>
                  {' '}
                  {t('previous')}
                </UI.Button>
                {' '}
                <UI.Button
                  onClick={() => next()}
                  sc={{ blank: true, size: 'xs', mr: 2 }}
                  disabled={limit >= events.length}
                >
                  {t('next')}
                  {' '}
                  <UI.Icon sc={{ muted: true }}>
                    <ChevronRight />
                  </UI.Icon>
                </UI.Button>
              </UI.Div>
            )}
          </>
        )}

        {!hasData && (
          <UI.Div>
            <UI.FlexContainer sc={{ justifyContent: 'center', mb: 2 }}>
              <img src={assetUrl('images/dashboard-repeaters.svg')} height={60} alt={t('returning_participants')} />
            </UI.FlexContainer>
            <UI.Div sc={{ textAlign: 'center', color: 'gray.400' }}>
              {t('no_data_available')}
            </UI.Div>
          </UI.Div>
        )}
      </UI.GridContainer>
    </UI.Card>
  );
});

export default RepeaterStats;
